import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { previewPosition, type PanelPlacement } from '../lib/hoverPreview.js';

// ~3× the previous 96px preview (Jonas QA #10 / WMS-11).
const PREVIEW_SIZE = 288;

/**
 * A small photo thumbnail that enlarges to a fixed-position preview on mouse
 * hover and opens the full image in a new tab when activated (WMS-54). Shared
 * by the stock dashboard, order detail and product recipe lists. `url` is an
 * already-signed image URL; renders nothing when it's null.
 */
export function ThumbButton({ url, alt }: { url: string | null; alt: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PanelPlacement | null>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  if (!url) return null;

  // Inline cells/cards live inside `overflow-x-auto` wrappers, which (per the
  // CSS spec) implicitly clip overflow-y too. Render the preview into a portal
  // at body-level fixed coords so it stays visible at the edges.
  const place = () => {
    const r = linkRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos(
      previewPosition(
        r,
        { width: window.innerWidth, height: window.innerHeight },
        PREVIEW_SIZE,
      ),
    );
  };

  return (
    <>
      <a
        ref={linkRef}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={alt}
        onPointerEnter={(e) => {
          if (e.pointerType === 'mouse') {
            place();
            setOpen(true);
          }
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') setOpen(false);
        }}
        onClick={() => setOpen(false)}
        onBlur={() => setOpen(false)}
        className="block shrink-0 cursor-zoom-in"
      >
        <img
          src={url}
          alt=""
          loading="lazy"
          className="h-10 w-10 md:h-8 md:w-8 rounded border border-border object-cover"
        />
      </a>
      {open && pos
        ? createPortal(
            <img
              src={url}
              alt={alt}
              // Render at the size `previewPosition` clamped to, not the
              // requested one — on a narrow phone the full 288px preview
              // doesn't fit between the margins the position assumes.
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: pos.width,
                height: pos.height,
                zIndex: 50,
              }}
              className="rounded border border-border bg-background object-cover shadow-lg pointer-events-none"
            />,
            document.body,
          )
        : null}
    </>
  );
}
